/**
 * Error types for the Math Exercise Engine
 */
import { ILexingError, IRecognitionException } from 'chevrotain';

/**
 * Source position information for error reporting
 */
export interface SourcePosition {
  startLine: number;
  startColumn: number;
  endLine: number;
  endColumn: number;
}

/**
 * Base error class for all engine errors
 */
export class EngineError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EngineError';
  }
}

/**
 * Error thrown when lexer fails to tokenize input
 */
export class LexerError extends EngineError {
  public readonly lexerErrors: ILexingError[];
  public readonly positions: SourcePosition[];

  constructor(errors: ILexingError[]) {
    const messages = errors.map(e => 
      `Line ${e.line}, Column ${e.column}: ${e.message}`
    );
    super(`Lexer errors:\n${messages.join('\n')}`);
    this.name = 'LexerError';
    this.lexerErrors = errors;
    this.positions = errors.map(e => ({
      startLine: e.line ?? 1,
      startColumn: e.column ?? 1,
      endLine: e.line ?? 1,
      endColumn: (e.column ?? 1) + (e.length ?? 1)
    }));
  }
}

/**
 * Error thrown when parser fails to parse tokens
 */
export class ParseError extends EngineError {
  public readonly parseErrors: IRecognitionException[];
  public readonly positions: SourcePosition[];

  constructor(errors: IRecognitionException[]) {
    const messages = errors.map(e => {
      const token = e.token;
      return `Line ${token.startLine}, Column ${token.startColumn}: ${e.message}`;
    });
    super(`Parse errors:\n${messages.join('\n')}`);
    this.name = 'ParseError';
    this.parseErrors = errors;
    this.positions = errors.map(e => ({
      startLine: e.token.startLine ?? 1,
      startColumn: e.token.startColumn ?? 1,
      endLine: e.token.endLine ?? 1,
      endColumn: e.token.endColumn ?? 1
    }));
  }
}

/**
 * Error thrown during AST transformation
 */
export class TransformError extends EngineError {
  public readonly position?: SourcePosition;

  constructor(message: string, position?: SourcePosition) {
    super(message);
    this.name = 'TransformError';
    this.position = position;
  }
}

/**
 * Error thrown during validation
 */
export class ValidationError extends EngineError {
  public readonly details: Record<string, string>;

  constructor(message: string, details: Record<string, string> = {}) {
    super(message);
    this.name = 'ValidationError';
    this.details = details;
  }
}
